import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, HeartOff, ChevronRight, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { getFavoriteProducts } from '../services/profileService';
import VerticalContentCard from '../components/Category/VerticalContentCard'; 
import { useAuthContext } from '../context/AuthContext'; 
import { convertToPersianNumbers } from '../utils/PersianNumberUtils';

const FavoritesFullPage = () => {
    const [favoritesData, setFavoritesData] = useState([]); // لیست کل محصولات
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false); // وضعیت بارگذاری صفحات بعدی
    const [isError, setIsError] = useState(false);
    const [nextPageUrl, setNextPageUrl] = useState(null); // نگهداری لینک صفحه بعد از بک‌اِند
    
    const { profileUpdateKey } = useAuthContext();

    // تابع اصلی فراخوانی داده‌ها
    const fetchFavorites = useCallback(async (url = null) => {
        try {
            if (!url) setIsLoading(true); else setIsFetchingMore(true);

            // فراخوانی API (اگر url داشتیم یعنی صفحه بعد، وگرنه صفحه اول)
            const responseData = await getFavoriteProducts(url);
            
            // ۱. استخراج داده‌ها با همان منطق ایمن قبلی
            const rawItems = responseData?.results || (Array.isArray(responseData) ? responseData : []);
            const products = rawItems
                .map(item => item?.product || item?.product_details || item)
                .filter(p => p !== null && typeof p === 'object');

            // ۲. آپدیت لیست: اگر صفحه اول بود جایگزین کن، اگر صفحات بعد بود به قبلی‌ها اضافه کن
            setFavoritesData(prev => url ? [...prev, ...products] : products);
            
            // ۳. ذخیره لینک صفحه بعدی از پاسخ Django
            setNextPageUrl(responseData?.next || null);
            
            setIsError(false);
        } catch (error) {
            console.error("PAGINATION ERROR:", error);
            setIsError(true);
        } finally {
            setIsLoading(false);
            setIsFetchingMore(false);
        }
    }, []);

    // اجرای اولیه با تغییر کلید آپدیت پروفایل
    useEffect(() => {
        fetchFavorites();
    }, [fetchFavorites, profileUpdateKey]);

    // هندلر دکمه "مشاهده بیشتر"
    const handleLoadMore = () => {
        if (nextPageUrl && !isFetchingMore) {
            fetchFavorites(nextPageUrl);
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col justify-center items-center min-h-[400px] rtl">
                <Loader2 className="w-10 h-10 animate-spin text-purple-700 mb-4" />
                <p className="text-gray-500 font-medium">در حال چیدمان لیست علاقه‌مندی‌ها...</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 rtl text-right ">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4 border-b pb-6">
                <div>
                    <nav className="flex items-center text-xs text-gray-400 mb-2 font-medium">
                        <Link to="/profile" className="hover:text-purple-600 transition">پروفایل کاربری</Link>
                        <ChevronRight className="w-3 h-3 mx-1 rotate-180" />
                        <span className="text-gray-600">علاقه‌مندی‌ها</span>
                    </nav>
                    <h1 className="text-xs font-black text-gray-800 tracking-tight">محتوای مورد علاقه من</h1>
                </div>
                
                <div className="bg-purple-50 text-purple-700 border border-purple-100 px-5 py-2 rounded-2xl flex items-center shadow-sm">
                    <span className="text-xl font-bold ml-2">{convertToPersianNumbers(favoritesData.length)}</span>
                    <span className="text-[11px] font-medium">مورد را لایک کرده‌اید</span>
                </div>
            </div>

            {/* Main Grid */}
            {favoritesData.length > 0 ? (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {favoritesData.map((product, index) => (
                            <div key={`${product.id}-${index}`} className="transition-all duration-300">
                                <VerticalContentCard data={product} />
                            </div>
                        ))}
                    </div>

                    {/* Load More Button */}
                    {nextPageUrl && (
                        <div className="flex justify-center mt-12 pb-10">
                            <button
                                onClick={handleLoadMore}
                                disabled={isFetchingMore}
                                className="flex items-center space-x-2 space-x-reverse bg-white border-2 border-purple-600 text-purple-600 px-8 py-3 rounded-xl font-bold hover:bg-purple-600 hover:text-white transition-all duration-300 disabled:opacity-50 shadow-lg shadow-purple-50"
                            >
                                {isFetchingMore ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>در حال بارگذاری...</span>
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-5 h-5" />
                                        <span>مشاهده موارد بیشتر</span>
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </>
            ) : (
                /* Empty State */
                <div className="flex flex-col items-center justify-center py-24 bg-gray-50 rounded-[2.5rem] border-2 border-dashed border-gray-100">
                    <HeartOff className="w-16 h-16 text-gray-300 mb-6" />
                    <h2 className="text-xl font-bold text-gray-800 mb-2">هنوز چیزی اضافه نکرده‌اید</h2>
                    <Link to="/products/list" className="mt-6 text-purple-700 font-bold underline">
                       برکشت به صفحه‌ی درس‌گفتارها
                    </Link>
                </div>
            )}
        </div>
    );
};

export default FavoritesFullPage;